import React, { useEffect, useState } from "react";
import SettingsSection from "../SettingsSection";
import { startLogin } from "../../utils/session";

const EMPTY_PROFILE = { avatarUrl: null, bannerUrl: null };

export default function KamiProfileSection({ guildId, apiBase, token }) {
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [nickname, setNickname] = useState("");
  const [bio, setBio] = useState("");
  const [globalUsername, setGlobalUsername] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(!token);

  useEffect(() => {
    if (!guildId) return undefined;
    if (!token) {
      setNeedsLogin(true);
      setError("Connect your Discord account to manage Kami's server profile.");
      return undefined;
    }

    setNeedsLogin(false);
    const controller = new AbortController();

    fetch(`${apiBase}/settings/${guildId}/kami-profile`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 401) {
            setNeedsLogin(true);
            throw new Error("Your Discord login expired. Reconnect to manage Kami's profile.");
          }
          if (response.status === 429) {
            const wait = Math.ceil(Number(data.retry_after) || 2);
            throw new Error(
              `Discord rate limit — wait about ${wait}s and try again.`,
            );
          }
          throw new Error(data.error || "Could not load Kami's profile.");
        }
        setProfile(data);
        setNickname(data.nickname || "");
        setBio(data.bio || "");
        setGlobalUsername(data.username || "");
        setError("");
      })
      .catch((loadError) => {
        if (loadError.name !== "AbortError") {
          setError(loadError.message || "Could not load Kami's profile.");
        }
      });

    return () => controller.abort();
  }, [apiBase, guildId, token]);

  const upload = async (kind, file) => {
    if (!file) return;
    setError("");
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Images must be 5 MB or smaller.");
      return;
    }

    setBusy(kind);
    try {
      const form = new FormData();
      form.append("image", file);
      const response = await fetch(
        `${apiBase}/settings/${guildId}/kami-profile/${kind}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: form,
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          setNeedsLogin(true);
          throw new Error("Your Discord login expired. Reconnect to manage Kami's profile.");
        }
        if (response.status === 429) {
          throw new Error("Discord rate limit — wait a moment and try again.");
        }
        throw new Error(data.error || "Image upload failed.");
      }
      setProfile((prev) => ({ ...prev, ...data }));
    } catch (uploadError) {
      setError(uploadError.message || "Image upload failed.");
    } finally {
      setBusy("");
    }
  };

  const reset = async (kind) => {
    setError("");
    setBusy(kind);
    try {
      const response = await fetch(
        `${apiBase}/settings/${guildId}/kami-profile/${kind}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          setNeedsLogin(true);
          throw new Error("Your Discord login expired. Reconnect to manage Kami's profile.");
        }
        throw new Error(data.error || "Could not reset the image.");
      }
      setProfile((prev) => ({ ...prev, ...data }));
    } catch (resetError) {
      setError(resetError.message || "Could not reset the image.");
    } finally {
      setBusy("");
    }
  };

  const saveNickname = async (nextNickname = nickname) => {
    const value = String(nextNickname || "").trim();
    if (value.length > 32) {
      setError("Nickname must be 32 characters or fewer.");
      return;
    }

    setError("");
    setBusy("nickname");
    try {
      const response = await fetch(
        `${apiBase}/settings/${guildId}/kami-profile/nickname`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ nickname: value }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          setNeedsLogin(true);
          throw new Error("Your Discord login expired. Reconnect to manage Kami's profile.");
        }
        if (response.status === 429) {
          throw new Error("Discord rate limit — wait a moment and try again.");
        }
        throw new Error(data.error || "Could not update Kami's server nickname.");
      }
      setNickname(data.nickname || "");
      setGlobalUsername(data.username || globalUsername);
      if (typeof data.bio === "string") setBio(data.bio);
    } catch (saveError) {
      setError(saveError.message || "Could not update Kami's server nickname.");
    } finally {
      setBusy("");
    }
  };

  const saveBio = async (nextBio = bio) => {
    const value = String(nextBio || "").trim();
    if (value.length > 190) {
      setError("Bio must be 190 characters or fewer.");
      return;
    }

    setError("");
    setBusy("bio");
    try {
      const response = await fetch(
        `${apiBase}/settings/${guildId}/kami-profile/bio`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ bio: value }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        if (response.status === 401) {
          setNeedsLogin(true);
          throw new Error("Your Discord login expired. Reconnect to manage Kami's profile.");
        }
        if (response.status === 429) {
          throw new Error("Discord rate limit — wait a moment and try again.");
        }
        throw new Error(data.error || "Could not update Kami's server bio.");
      }
      setBio(data.bio || "");
      if (typeof data.nickname === "string") setNickname(data.nickname);
    } catch (saveError) {
      setError(saveError.message || "Could not update Kami's server bio.");
    } finally {
      setBusy("");
    }
  };

  const imageControls = (kind, label, imageUrl) => (
    <div style={{ marginBottom: "18px" }}>
      <label className="input-label">{label}</label>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Current Kami ${kind}`}
          style={{
            display: "block",
            width: kind === "avatar" ? "76px" : "100%",
            maxWidth: "420px",
            height: kind === "avatar" ? "76px" : "110px",
            objectFit: "cover",
            borderRadius: kind === "avatar" ? "50%" : "6px",
            marginBottom: "10px",
          }}
        />
      ) : (
        <p className="help-text" style={{ margin: "0 0 10px" }}>
          Using Kami's default {kind}.
        </p>
      )}
      <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
        <label
          className="manage-btn"
          style={{
            width: "auto",
            minHeight: "42px",
            margin: 0,
            padding: "10px 18px",
            cursor: busy || needsLogin ? "not-allowed" : "pointer",
          }}
        >
          {busy === kind ? "Uploading…" : `Upload ${kind}`}
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif"
            disabled={Boolean(busy) || needsLogin}
            onChange={(event) => {
              upload(kind, event.target.files?.[0]);
              event.target.value = "";
            }}
            style={{ display: "none" }}
          />
        </label>
        {imageUrl ? (
          <button
            type="button"
            className="manage-btn"
            style={{
              width: "auto",
              minHeight: "42px",
              margin: 0,
              padding: "10px 18px",
            }}
            disabled={Boolean(busy) || needsLogin}
            onClick={() => reset(kind)}
          >
            Reset
          </button>
        ) : null}
      </div>
    </div>
  );

  return (
    <SettingsSection>
      <p className="help-text" style={{ marginBottom: "16px" }}>
        Optional add-on: set a server-specific nickname, bio, avatar, and banner
        for Kami with a one-time <strong>Server Host</strong> unlock (donate
        page). Anyone with <strong>Manage Server</strong> can edit it once
        unlocked. Changes apply only here.
      </p>
      <div style={{ marginBottom: "20px" }}>
        <label className="input-label" htmlFor="kami-server-nickname">
          SERVER NICKNAME
        </label>
        <p className="help-text" style={{ margin: "0 0 8px" }}>
          Changes Kami's display name only here. The global username stays
          {globalUsername ? ` ${globalUsername}.` : " unchanged."}
        </p>
        <input
          id="kami-server-nickname"
          className="settings-input"
          value={nickname}
          maxLength={32}
          disabled={Boolean(busy) || needsLogin}
          placeholder={globalUsername || "Kami"}
          onChange={(event) => setNickname(event.target.value)}
        />
        <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
          <button
            type="button"
            className="manage-btn"
            style={{ width: "auto", padding: "10px 18px" }}
            disabled={Boolean(busy) || needsLogin}
            onClick={() => saveNickname()}
          >
            {busy === "nickname" ? "Saving…" : "Save nickname"}
          </button>
          {nickname ? (
            <button
              type="button"
              className="manage-btn"
              style={{ width: "auto", padding: "10px 18px" }}
              disabled={Boolean(busy) || needsLogin}
              onClick={() => saveNickname("")}
            >
              Reset
            </button>
          ) : null}
        </div>
      </div>
      <div style={{ marginBottom: "20px" }}>
        <label className="input-label" htmlFor="kami-server-bio">
          SERVER BIO
        </label>
        <p className="help-text" style={{ margin: "0 0 8px" }}>
          Short About Me text shown on Kami&apos;s profile in this server only
          (max 190 characters).
        </p>
        <textarea
          id="kami-server-bio"
          className="settings-input"
          value={bio}
          maxLength={190}
          rows={3}
          disabled={Boolean(busy) || needsLogin}
          placeholder="About Kami in this server…"
          onChange={(event) => setBio(event.target.value)}
          style={{ resize: "vertical", minHeight: "72px" }}
        />
        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "8px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="manage-btn"
            style={{ width: "auto", padding: "10px 18px" }}
            disabled={Boolean(busy) || needsLogin}
            onClick={() => saveBio()}
          >
            {busy === "bio" ? "Saving…" : "Save bio"}
          </button>
          {bio ? (
            <button
              type="button"
              className="manage-btn"
              style={{ width: "auto", padding: "10px 18px" }}
              disabled={Boolean(busy) || needsLogin}
              onClick={() => saveBio("")}
            >
              Clear
            </button>
          ) : null}
          <span className="help-text" style={{ margin: 0 }}>
            {bio.length}/190
          </span>
        </div>
      </div>
      {imageControls("avatar", "SERVER AVATAR", profile.avatarUrl)}
      {imageControls("banner", "SERVER BANNER", profile.bannerUrl)}
      {error ? (
        <p role="alert" style={{ color: "#d95d66", fontSize: "0.85rem" }}>
          {error}
        </p>
      ) : null}
      {needsLogin ? (
        <button
          type="button"
          className="manage-btn"
          style={{ width: "auto", padding: "10px 18px" }}
          onClick={() => startLogin(`/settings/${guildId}`)}
        >
          Reconnect Discord
        </button>
      ) : null}
    </SettingsSection>
  );
}
